// First-party observability beacons (cheap, no Sentry). Admin-panel activity +
// client-error capture, POSTed to the Lambda. All best-effort: telemetry must
// never break the app, so everything is wrapped and failures are swallowed.
import { API_BASE } from "./api";

// The venue whose admin the operator is in, set by the admin layout so the
// global error/mutation handlers can attribute events without threading it
// through every call site. Null on the platform admin / public pages.
let currentVenueId: string | null = null;
export function setObservabilityVenue(id: string | null): void {
  currentVenueId = id;
}

function beacon(path: string, body: unknown): void {
  try {
    const json = JSON.stringify(body);
    const url = `${API_BASE}${path}`;
    // sendBeacon survives a page unload; fall back to keepalive fetch.
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([json], { type: "text/plain" }))) return;
    fetch(url, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: json, keepalive: true }).catch(() => {});
  } catch {
    /* never throw from telemetry */
  }
}

let lastAdminPath = "";
/** One event per admin page view (deduped against repeat resolutions). */
export function trackAdminPageview(path: string): void {
  if (!path.startsWith("/admin")) return;
  if (path === lastAdminPath) return;
  lastAdminPath = path;
  beacon("/api/admin-event", { venueId: currentVenueId ?? undefined, type: "pageview", path });
}

/** A key admin action (campaign saved, branding saved, slot added…). */
export function trackAdminAction(action: string): void {
  beacon("/api/admin-event", { venueId: currentVenueId ?? undefined, type: "action", action });
}

let lastErrorSig = "";
let lastErrorAt = 0;
/** Report a front-end error. Deduped (same message within 5s) so a render loop
 *  can't flood the endpoint. */
export function reportClientError(message: string, stack?: string): void {
  const msg = (message || "").slice(0, 1000);
  if (!msg) return;
  const now = Date.now();
  if (msg === lastErrorSig && now - lastErrorAt < 5000) return;
  lastErrorSig = msg;
  lastErrorAt = now;
  beacon("/api/client-error", {
    venueId: currentVenueId ?? undefined,
    path: typeof window !== "undefined" ? window.location.pathname : undefined,
    message: msg,
    stack: stack?.slice(0, 4000),
  });
}

/** Install window-level handlers (uncaught errors + unhandled rejections). */
export function installClientErrorCapture(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    reportClientError(e.message || String(e.error), e.error?.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    reportClientError(r instanceof Error ? r.message : String(r), r instanceof Error ? r.stack : undefined);
  });
}
